"use client";

// Invisible Cloudflare Turnstile widget. Render it on surfaces that need an
// anti-bot token (checkout, chat). It loads the Turnstile script lazily, renders
// in "interaction-only" mode (hidden unless a challenge is genuinely needed),
// and keeps the latest token in the module-level holder (lib/turnstile) for the
// API call sites to read.
//
// Dark by default: when NEXT_PUBLIC_TURNSTILE_SITEKEY is unset this renders
// null and loads nothing — so SEO/content pages are untouched until rollout.

import { useEffect, useRef } from "react";

import { setTurnstileToken, turnstileSiteKey } from "@/lib/turnstile";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function TurnstileGate() {
  const siteKey = turnstileSiteKey();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || typeof window === "undefined") return;
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !containerRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        // Keep the widget out of the customer UI. Cloudflare may still decide
        // to challenge suspicious traffic, but the normal state is no checkbox.
        // Do not pass size="invisible": current Turnstile only accepts
        // compact/flexible/normal for size; invisibility is controlled by
        // appearance="interaction-only".
        appearance: "interaction-only",
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(null),
        "error-callback": () => setTurnstileToken(null),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.head.appendChild(script);
    } else {
      poll = setInterval(() => {
        if (window.turnstile) {
          if (poll) clearInterval(poll);
          renderWidget();
        }
      }, 200);
    }

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
      setTurnstileToken(null);
    };
  }, [siteKey]);

  if (!siteKey) return null;
  // Render in a fixed, unobtrusive corner. `interaction-only` keeps the widget
  // invisible in the normal case; on the rare occasion Cloudflare forces an
  // interactive challenge it then appears as a small corner widget instead of a
  // banner at the top of the chat pushing all content down (the 2026-06-07
  // "Verify you are human" box). For ZERO visible UI ever, set the widget to
  // "Invisible" mode in the Cloudflare Turnstile dashboard — that is the
  // production-side fix and needs no deploy.
  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{ position: "fixed", right: 12, bottom: 12, zIndex: 50 }}
    />
  );
}
